import { createFileRoute, Link } from "@tanstack/react-router";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";

const CANONICAL = "https://virginiabariatric.com/resources/qualifications/print";
const REGULAR_URL = "https://virginiabariatric.com/resources/qualifications";

export const Route = createFileRoute("/resources/qualifications/print")({
  head: () => ({
    meta: [
      { title: "Do You Qualify for Bariatric Surgery? — Print | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Print-friendly guide to bariatric surgery eligibility: BMI thresholds by payor, qualifying conditions, East Asian adjustments, and ESG and balloon BMI windows.",
      },
      { name: "robots", content: "noindex, follow" },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Do You Qualify for Bariatric Surgery? — Print | Virginia Bariatric" },
        { property: "og:description", content: "Print-friendly guide to bariatric surgery eligibility: BMI thresholds by payor, qualifying conditions, East Asian adjustments, and ESG and balloon BMI windows." },
        { property: "og:type", content: "article" },
        { property: "og:url", content: CANONICAL },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: "Do You Qualify for Bariatric Surgery? — Print | Virginia Bariatric" },
        { name: "twitter:description", content: "Print-friendly guide to bariatric surgery eligibility: BMI thresholds by payor, qualifying conditions, East Asian adjustments, and ESG and balloon BMI windows." },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: [{ rel: "canonical", href: REGULAR_URL }],
  }),
  component: QualificationsPrintPage,
});

const thClass = "border border-border bg-muted/40 px-4 py-2 font-semibold text-left";
const tdClass = "border border-border px-4 py-2";
const tdHeadClass = "border border-border px-4 py-2 font-semibold";

function QualificationsPrintPage() {
  return (
    <>
      <style>{`
        @media print {
          nav[aria-label="Utility"],
          header,
          footer,
          .print-toolbar {
            display: none !important;
          }
          html {
            color-scheme: light !important;
          }
          body {
            background: white !important;
            color: black !important;
          }
          a {
            color: black !important;
            text-decoration: none !important;
          }
          .print-container {
            max-width: 100% !important;
            padding: 0 !important;
          }
          h1, h2, h3 {
            color: black !important;
            page-break-after: avoid;
          }
          p, li {
            orphans: 3;
            widows: 3;
          }
          ul, ol, table {
            page-break-inside: avoid;
          }
          .sources {
            page-break-before: auto;
          }
        }
      `}</style>

      <div className="print-toolbar mx-auto max-w-4xl px-6 pt-6 pb-2 print:hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 p-4">
          <p className="text-sm text-muted-foreground">
            Print-friendly version of the qualifications guide.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Print this page
            </button>
            <Link
              to="/resources/qualifications"
              className="rounded-full border border-input bg-background px-5 py-2 text-sm font-medium text-foreground hover:bg-accent"
            >
              View regular page
            </Link>
          </div>
        </div>
      </div>

      <main className="print-container mx-auto max-w-4xl px-6 py-8 print:py-0">
        <header className="mb-8 text-center print:mb-6">
          <h1 className="text-3xl font-serif text-brand-mid print:text-black md:text-4xl">
            Do You Qualify for Bariatric Surgery?
          </h1>
          <p className="mt-2 text-base text-muted-foreground print:text-black italic">
            It depends on your medical status, your BMI, and whether you plan to use insurance.
          </p>
          <p className="mt-1 text-sm text-muted-foreground print:text-black">
            Virginia Bariatric Surgery · (703) 709-9771 · {REGULAR_URL}
          </p>
          <p className="mt-1 text-sm text-muted-foreground print:text-black">
            Last reviewed August 18, 2026.
          </p>
        </header>

        <article className="leading-[1.35] text-base space-y-5 print:space-y-4">
          <p>
            If you want to know whether you qualify to have weight-loss surgery, the
            answer boils down to a few simple questions. Here is what we look at:
          </p>
          <ol className="list-decimal pl-6 space-y-1.5">
            <li>
              <strong>Your age</strong> — we offer weight loss surgery to people 18
              and over.
            </li>
            <li>
              <strong>Your medical and emotional fitness</strong> – is it a good time
              for life-changing surgery?
            </li>
            <li>
              <strong>Your weight and height</strong> — these figures tell us your
              body mass index (BMI).
            </li>
            <li>
              <strong>Obesity-related medical diseases</strong> — do you have any?
            </li>
            <li>
              <strong>Who is paying</strong> — are you using insurance or
              self-paying?
            </li>
          </ol>

          <h2 className="text-2xl font-serif text-brand-mid print:text-black text-center pt-2">
            Age and medical fitness
          </h2>
          <p>
            These guidelines apply to everyone, regardless of BMI or payor. They are
            threshold requirements. Meeting them means you can be considered for
            surgery. If you do not meet them, surgery cannot be offered. They include:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>You are 18 or older.</li>
            <li>
              You are healthy enough to undergo surgery and general anesthesia. This
              is assessed in the pre-op evaluation. We might need you to have cardiac
              or pulmonary testing, depending on your history.
            </li>
            <li>
              You are psychologically ready. It is confirmed with an evaluation from
              a qualified behavioral health professional.
            </li>
            <li>You do not have active substance abuse.</li>
            <li>
              You are not pregnant, and you are willing to postpone pregnancy for
              roughly 12 to 18 months after surgery.
            </li>
            <li>You understand the risks.</li>
            <li>
              You are willing to commit to lifelong dietary change, follow-up visits,
              and daily vitamin supplementation.
            </li>
          </ul>
          <p>
            Nearly everyone who wants surgery can meet these, though sometimes there
            is some work to do. A behavioral health issue or an untreated medical
            problem usually means a delay rather than a permanent no.
          </p>

          <h2 className="text-2xl font-serif text-brand-mid print:text-black text-center pt-2">
            BMI, medical disease, and who is paying
          </h2>
          <p>
            The requirements for BMI and medical conditions depend on the payor. If
            insurance is paying, these are the requirements:
          </p>

          <h3 className="text-xl font-serif text-brand-mid print:text-black text-center pt-1">
            If insurance is paying
          </h3>
          <table className="w-full border-collapse text-left text-base">
            <thead>
              <tr>
                <th className={thClass}>Your BMI</th>
                <th className={thClass}>Medical conditions necessary?</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={tdHeadClass}>40 or higher</td>
                <td className={tdClass}>No.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>35 – 39.9</td>
                <td className={tdClass}>Yes. Without a qualifying diagnosis, most plans will decline.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>30 – 34.9</td>
                <td className={tdClass}>Yes, but most plans do not cover surgery at this BMI at all. A rare few will cover it, if and only if type 2 diabetes is present.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>Under 30</td>
                <td className={tdClass}>Not a candidate for bariatric surgery.</td>
              </tr>
            </tbody>
          </table>

          <h3 className="text-xl font-serif text-brand-mid print:text-black text-center pt-1">
            If you are self-paying
          </h3>
          <table className="w-full border-collapse text-left text-base">
            <thead>
              <tr>
                <th className={thClass}>Your BMI</th>
                <th className={thClass}>Medical conditions necessary?</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={tdHeadClass}>40 or higher</td>
                <td className={tdClass}>No.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>35 – 39.9</td>
                <td className={tdClass}>No.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>30 – 34.9</td>
                <td className={tdClass}>Yes. You are a candidate if you have medical disease.</td>
              </tr>
              <tr>
                <td className={tdHeadClass}>Under 30</td>
                <td className={tdClass}>Not a candidate for bariatric surgery.</td>
              </tr>
            </tbody>
          </table>
          <p>
            If the two tables look different to you at the same BMI, you are reading
            them correctly. The reason is explained below.
          </p>

          <h3 className="text-xl font-serif text-brand-mid print:text-black text-center pt-1">
            Which conditions count
          </h3>
          <p>
            If your BMI is 35 to 39.9, your insurance company will not cover surgery
            unless you have a qualifying obesity-related condition. Here are the
            conditions that most plans accept:
          </p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Type 2 diabetes</li>
            <li>High blood pressure</li>
            <li>Obstructive sleep apnea</li>
            <li>Coronary artery disease or other heart disease</li>
            <li>High cholesterol or other lipid disorder</li>
            <li>Severe osteoarthritis or weight-bearing joint disease</li>
            <li>Fatty liver disease</li>
            <li>Severe gastroesophageal reflux</li>
            <li>Pulmonary hypertension or obesity hypoventilation syndrome</li>
          </ul>
          <p>
            If insurance is paying, they decide which medical problems can qualify
            you, and different insurers have different rules. It is wise to check if
            there is any doubt.
          </p>
          <p>
            Most insurers publish a medical policy that contains their rules. It can
            usually be found online by googling,{" "}
            "Medical policy for bariatric surgery for [my insurance company]."
          </p>
          <aside className="rounded-lg border border-brand bg-brand-soft p-4 my-6">
            <h3 className="text-xl font-serif text-brand-deep text-center mb-2">
              You may qualify without knowing it.
            </h3>
            <p className="text-brand-deep">
              Do not assume that you don’t have a qualifying condition. A qualifying
              diagnosis is often just waiting there for somebody to look for it.
            </p>
            <p className="mt-3 text-brand-deep">
              For example, studies have shown that over 70% of people whose BMI is
              between 35 and 39.9 have obstructive sleep apnea.<sup>1</sup> If you have
              never been evaluated for it, it is wise to check. You may already
              medically qualify without knowing it.
            </p>
          </aside>

          <h2 className="text-2xl font-serif text-brand-mid print:text-black text-center pt-2">
            Why the BMI thresholds are different if you have insurance
          </h2>
          <p>
            This is the part that confuses people most, and the explanation is
            straightforward.
          </p>
          <p>
            Most insurance criteria are based on a National Institutes of Health
            consensus statement published in 1991 — BMI 40 and above, or 35 and
            above with a comorbidity. Those thresholds have been embedded in payor
            policy for more than thirty years.
          </p>
          <p>
            Medical science has moved on. In 2022, the American Society for
            Metabolic and Bariatric Surgery and the International Federation for the
            Surgery of Obesity issued updated indications recommending surgery for
            anyone with a BMI of 35 or higher regardless of whether they have a
            related condition, and stating that surgery should be considered for
            people with metabolic disease at a BMI of 30 to 34.9.
          </p>
          <p>
            When you are self-paying, current clinical guidelines govern. When
            insurance is paying, the plan’s policy governs. Sadly, most plans still
            use the older thresholds.
          </p>

          <h2 className="text-2xl font-serif text-brand-mid print:text-black text-center pt-2">
            Two caveats worth knowing
          </h2>

          <h3 className="text-xl font-serif text-brand-mid print:text-black text-center pt-1">
            1. If you are of East Asian descent, the thresholds are lower
          </h3>
          <p>
            Body mass index does not carry the same metabolic risk across all
            populations. At any given BMI, people of East Asian descent tend to have
            more visceral fat and a higher risk of type 2 diabetes and
            cardiovascular disease than people of European descent at the same
            number.
          </p>
          <p>
            The 2022 ASMBS and IFSO guidelines account for this directly,
            recommending that BMI risk zones be adjusted to define obesity at a
            threshold of 25 to 27.5 in this population, and stating that access to
            surgery should not be denied solely on the basis of traditional BMI
            thresholds.
          </p>
          <p>
            In practice this means a patient of East Asian descent at a BMI of 32
            may be an entirely appropriate surgical candidate where the same number
            in another patient would not be. Insurance policies rarely recognize
            this adjustment, so it usually matters most in the self-pay
            conversation. It is worth raising at your consultation either way.
          </p>

          <h3 className="text-xl font-serif text-brand-mid print:text-black text-center pt-1">
            2. The transoral procedures have their own BMI windows — including a ceiling
          </h3>
          <p>
            Sleeve gastrectomy and gastric bypass have a floor but no upper limit.
            The endoscopic procedures, performed through the mouth with no
            incisions, work differently: they are approved for defined BMI ranges
            with both a lower and an upper bound. Above the ceiling, they are not
            the right tool.
          </p>
          <table className="w-full border-collapse text-left text-base">
            <thead>
              <tr>
                <th className={thClass}>Procedure</th>
                <th className={thClass}>BMI window</th>
                <th className={thClass}>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={tdHeadClass}>Endoscopic sleeve gastroplasty (ESG)</td>
                <td className={tdClass}>30 – 50</td>
                <td className={tdClass}>
                  FDA authorization specifies BMI 30–50 in adults who have not been
                  able to lose weight or maintain weight loss through more
                  conservative measures. Almost always self-pay.
                </td>
              </tr>
              <tr>
                <td className={tdHeadClass}>Gastric balloon (Orbera)</td>
                <td className={tdClass}>30 – 40</td>
                <td className={tdClass}>
                  FDA approval specifies BMI 30–40, prior conservative weight-loss
                  attempts, and participation in a supervised diet and behavior
                  program. The balloon stays in place for six months. Almost always
                  self-pay.
                </td>
              </tr>
            </tbody>
          </table>
          <p>
            Both endoscopic options are generally not covered by insurance, which
            means the self-pay table is the relevant one for them. They are worth
            discussing if your BMI falls in the 30 to 40 range, where surgical
            options may be limited by coverage and the endoscopic options are
            squarely on label.
          </p>

          <h2 className="text-2xl font-serif text-brand-mid print:text-black text-center pt-2">
            What to do next
          </h2>
          <ol className="list-decimal pl-6 space-y-1.5">
            <li>
              <strong>Calculate your BMI.</strong> It takes ten seconds and tells you
              which row you are in.
            </li>
            <li>
              <strong>Find out whether you have a qualifying condition.</strong> If
              you have not been evaluated for sleep apnea, diabetes, or fatty liver,
              you may have one and not know it.
            </li>
            <li>
              <strong>Call your insurer</strong> and ask two questions: does my plan
              include a bariatric surgery benefit, and what BMI and documentation
              does it require. If the benefit does not exist, that changes the whole
              conversation early rather than late.
            </li>
            <li>
              <strong>Come in for a consultation.</strong> We will confirm where you
              stand, order what is missing, and give you a straight answer about
              which options are actually open to you. Call (703) 709-9771 or request
              an appointment at https://virginiabariatric.com/appointments.
            </li>
          </ol>

          <p className="text-sm">
            This page is general information, not medical advice and not a guarantee
            of insurance benefits. Individual candidacy is determined through
            clinical evaluation, and coverage criteria vary by plan. Reviewed
            August 18, 2026.
          </p>
        </article>

        <section className="sources mt-10 border-t border-border pt-6 print:mt-8 print:pt-4">
          <h2 className="text-xl font-serif text-brand-mid print:text-black mb-4">
            Sources
          </h2>
          <ol className="list-decimal pl-6 space-y-2 text-base leading-[1.35]">
            <li>
              Lopez PP, Stefan B, Schulman CI, Byers PM. Prevalence of sleep apnea in morbidly obese patients who presented for weight loss surgery evaluation: more evidence for routine screening for obstructive sleep apnea before weight loss surgery. <em>Am Surg.</em> 2008 Sep;74(9):834-8. PMID: 18807673.
            </li>
            <li>
              Eisenberg D, Shikora SA, Aarts E, et al. 2022 American Society for
              Metabolic and Bariatric Surgery (ASMBS) and International Federation
              for the Surgery of Obesity and Metabolic Disorders (IFSO): Indications
              for Metabolic and Bariatric Surgery. <em>Surg Obes Relat Dis.</em> 2022.
            </li>
            <li>
              NIH Consensus Development Conference, Gastrointestinal Surgery for
              Severe Obesity, 1991 — the source of the BMI 40 / BMI
              35-with-comorbidity thresholds still used by most payors.
            </li>
            <li>
              FDA, ORBERA Intragastric Balloon System, Summary of Safety and
              Effectiveness Data, P140008 (indications for use, BMI 30–40).
            </li>
            <li>
              FDA, De Novo classification DEN210045, Apollo ESG and Apollo ESG Sx
              Systems (indications for use, BMI 30–50).
            </li>
            <li>ASMBS, Endorsed Procedures and FDA-Approved Devices.</li>
          </ol>
        </section>

        <footer className="mt-10 border-t border-border pt-4 text-center text-sm text-muted-foreground print:text-black print:mt-8">
          <p>
            Virginia Bariatric Surgery, P.C. · 1635 Penderbrook Drive, Fairfax, VA 22030 · (703) 709-9771
          </p>
          <p className="mt-1">
            {REGULAR_URL}
          </p>
        </footer>
      </main>
    </>
  );
}
